// Image optimization helper.
// cdn.magicpatterns.com is Imgix-backed and supports on-the-fly format
// conversion (WebP/AVIF via auto=format), compression (q) and resizing (w/h).
// This lets us serve correctly-sized, compressed WebP images WITHOUT
// re-uploading any assets or changing the visual result.
//
// Usage: optimizeImg(url, { w: 240, q: 65 })
interface ImgOpts {
  /** Target render width in CSS px. The DPR multiplier is applied for you. */
  w?: number
  /** Target render height in CSS px. */
  h?: number
  /** JPEG/WebP quality 0-100. Default 68 — visually lossless for these covers. */
  q?: number
  /** Device pixel ratio cap. Default 2 (retina). */
  dpr?: number
}

export function optimizeImg(url: string, opts: ImgOpts = {}): string {
  if (!url) return url
  // Only transform CDN-hosted assets we know support the params.
  if (!url.includes('cdn.magicpatterns.com')) return url

  const { w, h, q = 68, dpr = 2 } = opts
  const params = new URLSearchParams()
  // auto=format -> serves WebP/AVIF when the browser supports it.
  // auto=compress -> Imgix picks an efficient compression profile.
  params.set('auto', 'format,compress')
  params.set('q', String(q))
  if (w) params.set('w', String(Math.round(w * dpr)))
  if (h) params.set('h', String(Math.round(h * dpr)))
  if (w || h) params.set('fit', 'max')

  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}${params.toString()}`
}

// Builds a responsive srcset string with LITERAL pixel widths (no DPR
// multiplication), so the browser can pick the smallest image that still
// looks sharp for the device — small + crisp on mobile, larger on desktop.
export function buildSrcSet(url: string, widths: number[], q = 72): string {
  return widths
    .map((w) => `${optimizeImg(url, { w, q, dpr: 1 })} ${w}w`)
    .join(', ')
}
